import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, formatGBP, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, MagnifyingGlass } from "@phosphor-icons/react";

const STATUS = {
  intake: { label: "Intake", cls: "bg-slate-100 text-slate-700" },
  in_progress: { label: "In Progress", cls: "bg-blue-50 text-blue-800" },
  submitted: { label: "Submitted", cls: "bg-indigo-50 text-indigo-800" },
  awaiting_decision: { label: "Awaiting Decision", cls: "bg-amber-50 text-amber-800" },
  granted: { label: "Granted", cls: "bg-green-50 text-green-800" },
  refused: { label: "Refused", cls: "bg-red-50 text-red-800" },
  closed: { label: "Closed", cls: "bg-slate-100 text-slate-500" },
};

export default function MattersPage() {
  const [matters, setMatters] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ matter_types: [], matter_status: [] });
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  const emptyForm = {
    title: "", client_id: "", matter_type: "", status: "intake",
    assigned_to: "", fixed_fee: 0, deadline: "", description: "",
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [m, c, u, mt] = await Promise.all([
      api.get("/matters"), api.get("/clients"), api.get("/users"), api.get("/meta"),
    ]);
    setMatters(m.data); setClients(c.data); setUsers(u.data); setMeta(mt.data);
  };
  useEffect(() => { load(); }, []);

  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, fixed_fee: Number(form.fixed_fee) || 0 };
      if (!payload.assigned_to) payload.assigned_to = null;
      if (!payload.deadline) payload.deadline = null;
      if (!payload.description) payload.description = null;
      await api.post("/matters", payload);
      toast.success("Matter created");
      setOpen(false); setForm(emptyForm); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = matters.filter((m) => {
    const s = q.toLowerCase();
    const matchQ = !s || [m.title, m.reference, clientMap[m.client_id]?.full_name].some((v) => (v || "").toLowerCase().includes(s));
    const matchS = statusFilter === "all" || m.status === statusFilter;
    return matchQ && matchS;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-1">Caseload</div>
          <h2 className="font-serif-brand text-3xl">Matters</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="matters-search" placeholder="Search reference, title, client…" value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-8 rounded-sm h-9 w-72 border-slate-300" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="matters-status-filter" className="w-44 rounded-sm h-9"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-sm bg-white">
              <SelectItem value="all">All statuses</SelectItem>
              {meta.matter_status.map((s) => <SelectItem key={s} value={s}>{STATUS[s]?.label || s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-matter-button" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B] h-9">
                <Plus size={14} className="mr-1" /> New Matter
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-2xl">
              <DialogHeader><DialogTitle>Open a new matter</DialogTitle></DialogHeader>
              <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Matter title *</Label>
                  <Input data-testid="matter-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Client *</Label>
                  <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                    <SelectTrigger data-testid="matter-client" className="mt-1 rounded-sm"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent className="rounded-sm bg-white max-h-64">
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Type *</Label>
                  <Select value={form.matter_type} onValueChange={(v) => setForm({ ...form, matter_type: v })}>
                    <SelectTrigger data-testid="matter-type" className="mt-1 rounded-sm"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent className="rounded-sm bg-white max-h-64">
                      {meta.matter_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-sm bg-white">
                      {meta.matter_status.map((s) => <SelectItem key={s} value={s}>{STATUS[s]?.label || s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Assigned to</Label>
                  <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                    <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent className="rounded-sm bg-white">
                      {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Fixed fee (£)</Label>
                  <Input type="number" min="0" step="1" value={form.fixed_fee} onChange={(e) => setForm({ ...form, fixed_fee: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Deadline</Label>
                  <Input type="date" value={form.deadline || ""} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Description</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <DialogFooter className="md:col-span-2">
                  <Button data-testid="save-matter" type="submit" className="rounded-sm bg-[#0B1528] hover:bg-[#1E293B]">Create matter</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs tracking-wider uppercase text-slate-500">
            <tr>
              <th className="text-left px-6 py-3 font-medium">Reference</th>
              <th className="text-left px-6 py-3 font-medium">Matter</th>
              <th className="text-left px-6 py-3 font-medium">Client</th>
              <th className="text-left px-6 py-3 font-medium">Type</th>
              <th className="text-left px-6 py-3 font-medium">Status</th>
              <th className="text-left px-6 py-3 font-medium">Deadline</th>
              <th className="text-right px-6 py-3 font-medium">Fee</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="px-6 py-12 text-center text-slate-500">No matters found.</td></tr>
            )}
            {filtered.map((m) => (
              <tr key={m.id} data-testid={`matter-row-${m.id}`}
                className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/matters/${m.id}`)}>
                <td className="px-6 py-3 font-mono-num text-xs">{m.reference}</td>
                <td className="px-6 py-3">
                  <div className="font-medium">{m.title}</div>
                  <div className="text-xs text-slate-500">{userMap[m.assigned_to]?.name || "Unassigned"}</div>
                </td>
                <td className="px-6 py-3 text-slate-600">{clientMap[m.client_id]?.full_name || "—"}</td>
                <td className="px-6 py-3 text-slate-600">{m.matter_type}</td>
                <td className="px-6 py-3">
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-sm ${STATUS[m.status]?.cls || "bg-slate-100"}`}>
                    {STATUS[m.status]?.label || m.status}
                  </span>
                </td>
                <td className="px-6 py-3 text-slate-600">{formatDate(m.deadline)}</td>
                <td className="px-6 py-3 text-right font-mono-num">{formatGBP(m.fixed_fee)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
